import {
  Quaternion as ThreeQuaternion,
  Vector3,
  Matrix4,
  Quaternion,
} from 'three';
import type * as zod from 'zod';

import type { CartesianPose } from '@sb/geometry';
import {
  applyRelativeOffset,
  convertEulerPose,
  posesAreEqual,
  tooltipCoordinatesToBaseCoordinates,
  getPlaneOrientation,
  customBaseRotationToQuaternion,
  rotateVectorByQuaternion,
} from '@sb/geometry';
import { error as logError, info, namespace, warn } from '@sb/log';
import type { TCPOffsetOption } from '@sb/motion-planning';
import { ArmTarget, DEFAULT_TCP_OFFSET_OPTION } from '@sb/motion-planning';
import {
  getApproachLocations,
  getPickupLocations,
  getPickupLocationsInOrder,
} from '@sb/palletizing/PalletBoxesUtil';
import type { Space, StepFailure } from '@sb/routine-runner';
import { FailureKind } from '@sb/routine-runner';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';

import Step from '../Step';

import Arguments from './Arguments';
import Variables from './Variables';

type Arguments = zod.infer<typeof Arguments>;

type Variables = zod.infer<typeof Variables>;

const ns = namespace('WaypointStep');
export default class WaypointStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public initializeVariableState(): void {
    const { completedCount = 0, stoppedEarly = 0 } =
      this.variablesForInitialization;

    this.variables = {
      completedCount,
      currentActivity: 'none',
      stoppedEarly,
    };
  }

  /**
   * Evaluates the stop early condition if one is configured.
   * Also updates the stoppedEarly variable to track which OR group was met.
   */
  public async evaluateStopEarlyCondition(): Promise<boolean> {
    if (!this.hasStopEarlyConditions()) {
      return false;
    }

    try {
      const { result, matchedBranch } =
        await this.routineContext.evaluateConditionalWithBranch(
          this.args.stopEarlyConditions!,
        );

      if (result && matchedBranch !== undefined) {
        this.setVariable('stoppedEarly', matchedBranch);
      } else {
        this.setVariable('stoppedEarly', 0);
      }

      return result;
    } catch (err) {
      logError(
        ns`evaluateStopEarlyCondition`,
        `error evaluating stop early condition`,
        err,
      );

      // If evaluation fails, don't stop early
      return false;
    }
  }

  /**
   * Returns true if this waypoint has stop early conditions configured
   */
  public hasStopEarlyConditions(): boolean {
    return Boolean(this.args.stopEarlyConditions);
  }

  /**
   * Placeholder method for the play logic.
   * Implement the actual logic here.
   */
  public async _play(): Promise<void> {
    // TODO: Implement the play logic
  }

  // Utility to recursively find a step by id in a tree of steps
  private findStepById(steps: any[], id: string): any | undefined {
    for (const step of steps) {
      if (step.id === id) return step;

      if (step.steps && Array.isArray(step.steps)) {
        const found = this.findStepById(step.steps, id);
        if (found) return found;
      }
    }

    return undefined;
  }

  // Helper to resolve positionListID as either a space item or a waypoint step
  private async resolvePositionListReference(
    positionListID: string,
    positionListIndex: number,
    previousTargetPose: CartesianPose,
  ): Promise<any> {
    if (positionListID === 'current-position') {
      return { pose: previousTargetPose };
    }

    if (
      positionListID === 'expression' &&
      this.args.target &&
      'expression' in this.args.target &&
      this.args.target.expression
    ) {
      const expressionTarget = await this.routineContext.evaluateExpression(
        this.args.target.expression,
      );

      if (!expressionTarget) {
        logError(ns`resolvePositionListReference`, 'expression target is null');

        throw new Error(
          'Relative waypoint expression evaluated to an invalid value, please check expression',
        );
      }

      if (Array.isArray(expressionTarget)) {
        const list = expressionTarget as any[];

        if (list.length === 0) {
          throw new Error(
            'Relative waypoint expression evaluated to an empty list',
          );
        }

        const idx = positionListIndex % list.length;

        return list[idx];
      }

      return expressionTarget;
    }

    // Try as space item
    try {
      const spaceItem = this.routineContext.getSpaceItem(positionListID);

      if (spaceItem && Array.isArray(spaceItem.positions)) {
        if (spaceItem.positions.length > 0) {
          return spaceItem.positions[
            positionListIndex % spaceItem.positions.length
          ];
        }

        if (spaceItem.kind === 'palletBoxes') {
          const { palletConfig } = this.args;
          const relevantSpaceItems: Space.Item[] = [];

          if (spaceItem.palletBaseIDs) {
            spaceItem.palletBaseIDs.forEach((palletBaseID) => {
              relevantSpaceItems.push(
                this.routineContext.getSpaceItem(palletBaseID),
              );
            });
          }

          // Handle pickup position with specific box type
          if (
            palletConfig.moveType === 'pickup' &&
            palletConfig.selectedBoxType
          ) {
            const boxType = spaceItem.boxTypes.find(
              (bt) => bt.id === palletConfig.selectedBoxType,
            );

            if (!boxType) {
              throw new Error(
                `Box type not found: ${palletConfig.selectedBoxType}`,
              );
            }

            // Check if the box type has a pickup position defined
            if (!boxType.pickupPosition) {
              throw new Error(
                `Box type "${boxType.name}" does not have a pickup position defined. Please configure a pickup position for this box type.`,
              );
            }

            return boxType.pickupPosition;
          }

          // Handle approach/box positions with layer and box expressions
          if (
            palletConfig.selectedLayer != null &&
            palletConfig.selectedBox != null
          ) {
            const palletBaseID =
              palletConfig.selectedPalletBaseID || spaceItem.palletBaseIDs[0];

            const boxPickupPositionsMap =
              palletConfig.moveType === 'approach'
                ? getApproachLocations(
                    spaceItem,
                    relevantSpaceItems,
                    palletBaseID,
                  )
                : getPickupLocations(
                    spaceItem,
                    relevantSpaceItems,
                    palletBaseID,
                  );

            const layer = (await this.routineContext.evaluateExpression(
              palletConfig.selectedLayer,
            )) as number;

            const box = (await this.routineContext.evaluateExpression(
              palletConfig.selectedBox,
            )) as number;

            if (typeof layer !== 'number' || typeof box !== 'number') {
              throw new Error('Invalid layer or box');
            }

            // Validate layer and box indices
            if (layer < 1 || layer > spaceItem.layerOrder.length) {
              throw new Error(
                `Invalid layer index: ${layer}. Must be between 1 and ${spaceItem.layerOrder.length}`,
              );
            }

            const layerID = spaceItem.layerOrder[layer - 1];

            const layerPattern = spaceItem.layerPatterns.find(
              (lp) => lp.id === layerID,
            );

            if (!layerPattern) {
              throw new Error(
                `Layer pattern not found for layer ID: ${layerID}`,
              );
            }

            if (box < 1 || box > layerPattern.boxList.length) {
              throw new Error(
                `Invalid box index: ${box}. Must be between 1 and ${layerPattern.boxList.length}`,
              );
            }

            const boxID = layerPattern.boxList.find((b) => b.order === box)?.id;

            const key = `${layerID}/${boxID}/${layer - 1}`;
            const boxPickupPosition = boxPickupPositionsMap.get(key);

            if (!boxPickupPosition) {
              throw new Error(
                `No pickup position found for key: ${key}. This suggests a mismatch between the key construction and the map contents.`,
              );
            }

            return boxPickupPosition;
          }

          const palletBaseID =
            palletConfig.selectedPalletBaseID || spaceItem.palletBaseIDs[0];

          const boxPickupPositions = getPickupLocationsInOrder(
            spaceItem,
            relevantSpaceItems,
            palletBaseID,
            palletConfig.moveType === 'approach',
          );

          return boxPickupPositions[
            positionListIndex % boxPickupPositions.length
          ];
        }
      }
    } catch (e) {
      // Not a space item, fall through
    }

    // Try as waypoint step (recursively search the tree)
    const rootSteps = this.routineContext.loadedRoutineState?.steps || [];
    const step = this.findStepById(rootSteps, positionListID);

    if (step && step.args) {
      const { args } = step;

      // Check the target structure to determine the type
      if (args.target) {
        // Check if target has positionListID (position list or relative waypoint)
        if ('positionListID' in args.target) {
          // Recursively resolve the position list reference
          return this.resolvePositionListReference(
            args.target.positionListID,
            args.target.positionListIndex ?? positionListIndex,
            previousTargetPose,
          );
        }

        // Check if target has expression
        if ('expression' in args.target) {
          const expressionTarget = await this.routineContext.evaluateExpression(
            args.target.expression,
            undefined,
            true,
          );

          return Array.isArray(expressionTarget)
            ? expressionTarget[0]
            : expressionTarget;
        }

        // If target has pose or jointAngles, it's a single position
        if ('pose' in args.target || 'jointAngles' in args.target) {
          return args.target;
        }
      }
    }

    throw new Error(
      `Could not resolve positionListID "${positionListID}" as either a space item or a waypoint step`,
    );
  }

  // Helper to evaluate offset expressions and convert to Euler pose
  private async evaluateOffsetExpressions(): Promise<any | null> {
    const { offsetX, offsetY, offsetZ, offsetRx, offsetRy, offsetRz } =
      this.args.relativeConfig;

    // Evaluate all offset expressions
    const [x, y, z, rx, ry, rz] = await Promise.all([
      this.evaluateOffsetExpression(offsetX, true),
      this.evaluateOffsetExpression(offsetY, true),
      this.evaluateOffsetExpression(offsetZ, true),
      this.evaluateOffsetExpression(offsetRx, false),
      this.evaluateOffsetExpression(offsetRy, false),
      this.evaluateOffsetExpression(offsetRz, false),
    ]);

    // Check if any offsets were evaluated
    if (
      x === null &&
      y === null &&
      z === null &&
      rx === null &&
      ry === null &&
      rz === null
    ) {
      return null;
    }

    // Create EulerPose with capitalized property names (use 0 for undefined values)
    return {
      X: x ?? 0,
      Y: y ?? 0,
      Z: z ?? 0,
      Rx: rx ?? 0,
      Ry: ry ?? 0,
      Rz: rz ?? 0,
    };
  }

  // Helper to evaluate a single offset expression
  private async evaluateOffsetExpression(
    expression: any,
    isPositional: boolean,
  ): Promise<number | null> {
    if (!expression) {
      return null;
    }

    try {
      const result = await this.routineContext.evaluateExpression(expression);

      if (typeof result === 'number') {
        // For positional offsets, convert from the specified distance unit to meters
        if (isPositional && this.args.distanceUnit !== 'meter') {
          return this.convertDistanceToMeters(result, this.args.distanceUnit);
        }

        return result;
      }

      // If result is not a number, try to parse it
      const parsed = parseFloat(String(result));

      if (!Number.isNaN(parsed)) {
        if (isPositional && this.args.distanceUnit !== 'meter') {
          return this.convertDistanceToMeters(parsed, this.args.distanceUnit);
        }

        return parsed;
      }

      return null;
    } catch (error) {
      // If evaluation fails, return null (no offset)
      return null;
    }
  }

  // Helper to convert distance from specified unit to meters
  private convertDistanceToMeters(
    value: number,
    unit: 'meter' | 'millimeter' | 'inch',
  ): number {
    switch (unit) {
      case 'millimeter':
        return value / 1000;
      case 'inch':
        return value * 0.0254;
      case 'meter':
      default:
        return value;
    }
  }

  // Helper to apply an offset pose in the specified frame
  private applyOffsetToPose(pose: any, offsetQuatPose: any, frame: string) {
    switch (frame) {
      case 'base': {
        try {
          const { equipmentItems } = this.routineContext.equipment;

          const customBase = equipmentItems.find(
            (item) => item.config.kind === 'CustomBase' && item.isEnabled,
          );

          if (
            customBase &&
            customBase.config &&
            'rotation' in customBase.config
          ) {
            const quaternion = customBaseRotationToQuaternion(
              customBase.config.rotation,
            );

            // When using a custom base, the robot's coordinate system is rotated
            // So we need to transform the offset to the robot's actual coordinate system
            // before applying it to the pose

            // Transform the offset from world coordinates to robot base coordinates
            const offsetInRobotFrame = rotateVectorByQuaternion(
              { x: offsetQuatPose.x, y: offsetQuatPose.y, z: offsetQuatPose.z },
              quaternion,
            );

            // Create a new offset pose in the robot's coordinate system
            const robotFrameOffset = {
              ...offsetQuatPose,
              x: offsetInRobotFrame.x,
              y: offsetInRobotFrame.y,
              z: offsetInRobotFrame.z,
            };

            // Apply the transformed offset to the pose
            return applyRelativeOffset(pose, robotFrameOffset);
          }

          return applyRelativeOffset(pose, offsetQuatPose);
        } catch (error) {
          this.routineContext.fail({
            failure: { kind: FailureKind.InvalidRoutineLoadedFailure },
            failureReason: 'Invalid custom base configuration',
            error,
          });

          return applyRelativeOffset(pose, offsetQuatPose);
        }
      }
      case 'tool': {
        const poseQ = new ThreeQuaternion(pose.i, pose.j, pose.k, pose.w);
        const posePosition = new Vector3(pose.x, pose.y, pose.z);

        const offsetQuatPoseTooltip =
          tooltipCoordinatesToBaseCoordinates(offsetQuatPose);

        const offsetQ = new ThreeQuaternion(
          offsetQuatPoseTooltip.i,
          offsetQuatPoseTooltip.j,
          offsetQuatPoseTooltip.k,
          offsetQuatPoseTooltip.w,
        );

        const offsetPosition = new Vector3(
          offsetQuatPoseTooltip.x,
          offsetQuatPoseTooltip.y,
          offsetQuatPoseTooltip.z,
        );

        const poseMatrix = new Matrix4()
          .makeRotationFromQuaternion(poseQ)
          .setPosition(posePosition);

        const offsetMatrix = new Matrix4()
          .makeRotationFromQuaternion(offsetQ)
          .setPosition(offsetPosition);

        const finalMatrix = poseMatrix.clone().multiply(offsetMatrix);
        const translation = new Vector3();
        const rotation = new Quaternion();
        const scale = new Vector3();
        finalMatrix.decompose(translation, rotation, scale);

        const poseToolFinal = {
          x: translation.x,
          y: translation.y,
          z: translation.z,
          i: rotation.x,
          j: rotation.y,
          k: rotation.z,
          w: rotation.w,
        } as CartesianPose;

        return poseToolFinal;
      }
      case 'world': {
        return applyRelativeOffset(pose, offsetQuatPose);
      }
      default: {
        // Try to find a plane space item with this frame ID
        try {
          const spaceItem = this.routineContext.getSpaceItem(frame);

          if (spaceItem.kind === 'plane' && spaceItem.positions.length >= 3) {
            // Create a plane from the space item positions
            const plane = {
              origin: new Vector3(
                spaceItem.positions[0].pose.x,
                spaceItem.positions[0].pose.y,
                spaceItem.positions[0].pose.z,
              ),
              plusX: new Vector3(
                spaceItem.positions[1].pose.x,
                spaceItem.positions[1].pose.y,
                spaceItem.positions[1].pose.z,
              ),
              plusY: new Vector3(
                spaceItem.positions[2].pose.x,
                spaceItem.positions[2].pose.y,
                spaceItem.positions[2].pose.z,
              ),
            };

            // Get the plane's orientation quaternion
            const baseToPlane = getPlaneOrientation(plane);

            // Transform the orientation of the offset to the plane's coordinate system
            const offsetQ = new ThreeQuaternion(
              offsetQuatPose.i,
              offsetQuatPose.j,
              offsetQuatPose.k,
              offsetQuatPose.w,
            );

            // offsetQ now represents rotation baseToPlane * offsetOrientation
            offsetQ.premultiply(baseToPlane);

            // offsetPosition is the offset position in plane coordinates
            // so they need to happen in the plane frame and are
            // decoupled from the offsetQ rotation.
            const offsetPosition = new Vector3(
              offsetQuatPose.x,
              offsetQuatPose.y,
              offsetQuatPose.z,
            );

            // Create transformation matrix to apply offset in plane frame
            const rotMatrixBaseToPlane =
              new Matrix4().makeRotationFromQuaternion(baseToPlane);

            // Translation matrix for the offset position
            const transMatrixOffsetPosition = new Matrix4().makeTranslation(
              offsetPosition.x,
              offsetPosition.y,
              offsetPosition.z,
            );

            // Multiply rotation and translation matrices to get final transformation matrix
            // in global frame to extract translation
            const finalMatrixTranslationOffsetInGlobalFrame =
              rotMatrixBaseToPlane.multiply(transMatrixOffsetPosition);

            const translation = new Vector3();
            const rotation_tmp = new Quaternion();
            const scale_tmp = new Vector3();

            finalMatrixTranslationOffsetInGlobalFrame.decompose(
              translation,
              rotation_tmp,
              scale_tmp,
            );

            // since offsetQ already has baseToPlane applied,
            // we have to take only portion of the rotation that represents
            // the rotation in the base frame.
            const rotation = offsetQ
              .clone()
              .multiply(baseToPlane.clone().invert());

            // Copy the transformed orientation back to the offset pose
            const offsetInPlane = {
              x: translation.x,
              y: translation.y,
              z: translation.z,
              i: rotation.x,
              j: rotation.y,
              k: rotation.z,
              w: rotation.w,
            };

            return applyRelativeOffset(pose, offsetInPlane);
          }
        } catch (e) {
          // Plane not found, fall through to default behavior
        }

        // Default behavior if frame is not recognized or plane not found
        return applyRelativeOffset(pose, offsetQuatPose);
      }
    }
  }

  public async getArmTarget({
    effectiveTCPOption = undefined,
    parentCompletedCount,
    previousTargetPose,
    forcePoseGeneration = false,
    // Resolves the read/write frame for an `auto` current-position waypoint.
    // Its presence (`!= null`) is also the gate: it is non-null only when
    // MoveArmToV2 read the `matchRelativeToCurrentFrames` flag on at step start,
    // so the consumption side keys off param-presence (single flag read).
    capturedGlobalTCPOption = undefined,
    previousTargetTCPOption = undefined,
  }: {
    effectiveTCPOption?: TCPOffsetOption;
    parentCompletedCount: number;
    previousTargetPose: CartesianPose;
    forcePoseGeneration?: boolean;
    capturedGlobalTCPOption?: TCPOffsetOption;
    previousTargetTCPOption?: TCPOffsetOption;
  }): Promise<
    | {
        target: ArmTarget;
        poseForPreviousTarget: CartesianPose;
        poseForPreviousTargetTCPOption: TCPOffsetOption | undefined;
      }
    | StepFailure
  > {
    // 'positionListID' === 'current-position' is the relative-to-current case.
    // Hoisted here because the carry-tag rule (joint-match branch) and the
    // read/write frame selection below both key off it.
    const isCurrentPosition =
      'positionListID' in this.args.target &&
      this.args.target.positionListID === 'current-position';

    // A current-position waypoint reads the carried pose and commands the
    // target in the same TCP frame. The gate is purely param-presence —
    // `capturedGlobalTCPOption != null` is true only when MoveArmToV2 read the
    // `matchRelativeToCurrentFrames` flag on at step start (single read by
    // design). `!effectiveTCPOption` excludes the weld-forced path.
    const useReadWriteFrame =
      isCurrentPosition &&
      !effectiveTCPOption &&
      capturedGlobalTCPOption != null;

    // The read/write frame: `auto` resolves to the captured global (active
    // tool); a custom selector resolves to its own pick. Same frame for read
    // and command. `undefined` when the gate is off.
    let readWriteFrame: TCPOffsetOption | undefined;

    if (useReadWriteFrame) {
      readWriteFrame =
        this.args.tcpOption === 'auto'
          ? capturedGlobalTCPOption
          : this.args.tcpOption;
    }

    const parseTarget = (
      armPosition: any,
    ): {
      target: ArmTarget;
      pose: CartesianPose;
      tcpOptionTag: TCPOffsetOption | undefined;
    } => {
      const target = {
        motionKind: this.args.motionKind,
        ...armPosition,
        calibrationPose: armPosition.pose,
        // Parent blend config is applied in MoveArmToV2/Step.ts
        blendRadius: this.args.blendConfig.radius,
        stopHere: this.args.stopHere,
      };

      // Create a copy of the pose to avoid reference issues
      const pose = { ...armPosition.pose };

      // The TCP frame that `pose` (== poseForPreviousTarget for non-Cartesian
      // joint-match) is genuinely expressed in. `undefined` means "frame
      // unknown" — the read below fails loud on it rather than guessing.
      let tcpOptionTag: TCPOffsetOption | undefined;

      if (this.args.shouldMatchJointAngles && !forcePoseGeneration) {
        // If shouldMatchJointAngles is true, we only use joint angles
        target.pose = undefined;

        if (!getFeatureFlagSync('localAccuracyCalibration', false)) {
          target.calibrationPose = undefined;
        }

        // Joint-match: `poseForPreviousTarget` keeps the resolved
        // `armPosition.pose` (Zod strips the now-undefined `pose` key, so the
        // `'pose' in target` carry-out below does NOT overwrite it). The frame
        // that pose was stored in is its stored `tcpOption` — which may be
        // genuinely absent for legacy data (then the tag is `undefined`, NOT a
        // silent `'wrist'` default). The exception is `current-position`: there
        // `armPosition.pose` IS the incoming carry, so the tag is the incoming
        // `previousTargetTCPOption`.
        tcpOptionTag = isCurrentPosition
          ? previousTargetTCPOption
          : armPosition.tcpOption;
      } else {
        // remove jointAngle fields
        target.jointAngles = undefined;
        target.calibrationPose = undefined;

        // set effectiveTCPOption
        if (effectiveTCPOption) {
          // weld waypoints use the effectiveTCPOption from the parent weld step
          target.effectiveTCPOption = effectiveTCPOption;
        } else if (this.args.tcpOption === 'auto') {
          if (useReadWriteFrame) {
            // An `auto` current-position waypoint commands the active tool (the
            // captured global) — not `'wrist'` — so the read and command frames
            // match (the dive fix).
            target.effectiveTCPOption = readWriteFrame;
          } else if ('tcpOption' in target && target.tcpOption) {
            // use tcpOption that target was originally created with
            // we can leave target.tcpOption as is.
            target.effectiveTCPOption = target.tcpOption;
          } else {
            // use default tcpOption
            target.effectiveTCPOption = DEFAULT_TCP_OFFSET_OPTION;
          }
        } else {
          // use tcpOption that was specified in the Waypoint step
          target.effectiveTCPOption = this.args.tcpOption;
          target.gripperID = this.args.gripperID;
        }

        // Cartesian/tooltip pose produced: the carried pose is interpreted by
        // downstream `subtractEquipmentOffset` in `effectiveTCPOption`, so that
        // is the frame the carry is genuinely in.
        tcpOptionTag = target.effectiveTCPOption;
      }

      const parsedTarget = ArmTarget.safeParse(target);

      if (!parsedTarget.success) {
        throw new Error('Move arm target is not valid');
      }

      return { target: parsedTarget.data, pose, tcpOptionTag };
    };

    let target: ArmTarget;
    let anchor: any; // Space Item
    let poseForPreviousTarget: CartesianPose;
    let poseForPreviousTargetTCPOption: TCPOffsetOption | undefined;

    try {
      if ('positionListID' in this.args.target) {
        const { positionListID, positionListIndex } = this.args.target;
        const index = positionListIndex ?? parentCompletedCount;

        const armPosition = await this.resolvePositionListReference(
          positionListID,
          index,
          previousTargetPose,
        );

        info(ns`getArmTarget`, 'Resolved armPosition (positionListID)', {
          positionListID,
          index,
          pose: armPosition?.pose,
        });

        if (useReadWriteFrame) {
          // Fail loud on an unknown predecessor frame. If the integrator tag is
          // undefined (a legacy joint-match predecessor that stored no
          // `tcpOption`), the read's from-frame is unknown. A wrong from-frame is
          // a fresh dive of tool-offset magnitude, so we refuse to guess — do NOT
          // fall back to `capturedGlobalTCPOption` (a silent guess that dives if
          // wrong). Reachable only on the flag-on path.
          if (previousTargetTCPOption == null) {
            // Log the feature-flag detail for our own diagnosis; the
            // operator-facing failureReason stays free of internal flag names.
            warn(
              ns`getArmTarget`,
              'relative-to-current: predecessor waypoint has no stored TCP ' +
                'frame (legacy joint-match data); failing loud. Disable the ' +
                'matchRelativeToCurrentFrames feature flag to revert.',
              { waypointIndex: index },
            );

            return {
              failure: { kind: FailureKind.InvalidRoutineLoadedFailure },
              failureReason:
                "Relative-to-current move can't determine the reference TCP " +
                'frame of the preceding waypoint — re-teach the preceding ' +
                'waypoint.',
              error: new Error(
                'relative-to-current: unknown predecessor TCP frame',
              ),
            };
          }

          // Re-express the carried base pose from the integrator tag's frame
          // (`previousTargetTCPOption`) into the command frame, BEFORE the
          // relative-offset application below, so the offset lands in the command
          // frame. For current-position `armPosition.pose` IS the incoming carry
          // (resolvePositionListReference returns { pose: previousTargetPose }),
          // so this re-expresses the carry in place. The from-frame is the
          // integrator tag (narrowed to non-null by the guard above).
          armPosition.pose = this.routineContext.reexpressTooltipPose(
            armPosition.pose,
            previousTargetTCPOption,
            readWriteFrame as TCPOffsetOption,
          );
        }

        const parseResult = parseTarget(armPosition);
        target = parseResult.target;
        poseForPreviousTarget = parseResult.pose;
        poseForPreviousTargetTCPOption = parseResult.tcpOptionTag;

        // Anchor logic (optional, only for space items)
        try {
          const targetSpace = this.routineContext.getSpaceItem(positionListID);

          if (targetSpace.anchoredToID) {
            anchor = this.routineContext.getSpaceItem(targetSpace.anchoredToID);
          }
        } catch (e) {
          // Not a space item, skip anchor
        }
      } else if ('expression' in this.args.target) {
        const expressionTarget = await this.routineContext.evaluateExpression(
          this.args.target.expression,
          undefined,
          true,
        );

        // if expression evaluates to an array, use the first item
        // this makes it easier to use 'SinglePosition' space objects.
        const armPosition = Array.isArray(expressionTarget)
          ? expressionTarget[0]
          : expressionTarget;

        const parseResult = parseTarget(armPosition);
        target = parseResult.target;
        poseForPreviousTarget = parseResult.pose;
        poseForPreviousTargetTCPOption = parseResult.tcpOptionTag;
      } else {
        const parseResult = parseTarget(this.args.target);
        target = parseResult.target;
        poseForPreviousTarget = parseResult.pose;
        poseForPreviousTargetTCPOption = parseResult.tcpOptionTag;
      }

      // Check if any offset expressions are present
      const hasOffsets =
        this.args.relativeConfig.offsetX ||
        this.args.relativeConfig.offsetY ||
        this.args.relativeConfig.offsetZ ||
        this.args.relativeConfig.offsetRx ||
        this.args.relativeConfig.offsetRy ||
        this.args.relativeConfig.offsetRz;

      if (
        'pose' in target &&
        hasOffsets &&
        (!this.args.shouldMatchJointAngles || forcePoseGeneration)
      ) {
        // Application of relative configuration offset
        const offsetValues = await this.evaluateOffsetExpressions();

        if (offsetValues) {
          const offsetQuatPose = convertEulerPose.toCartesian(offsetValues);

          // Apply offset to pose for cartesian waypoints
          const offsetResult = this.applyOffsetToPose(
            target.pose,
            offsetQuatPose,
            this.args.relativeConfig.frame,
          );

          if (
            offsetResult &&
            typeof offsetResult === 'object' &&
            'failure' in offsetResult
          ) {
            return {
              failure: {
                kind: FailureKind.InvalidRoutineLoadedFailure,
              },
              failureReason: (offsetResult as any).failureReason,
              error: (offsetResult as any).error,
            };
          }

          target.pose = offsetResult;
        }
      }

      if (
        'positionListID' in this.args.target &&
        anchor &&
        anchor.kind === 'localAccuracyCalibration'
      ) {
        if ('pose' in target) {
          target.pose = this.routineContext.getCalibratedGoal(
            target.pose,
            anchor,
          );
        } else if ('jointAngles' in target && 'calibrationPose' in target) {
          const calibratedPose = this.routineContext.getCalibratedGoal(
            target.calibrationPose,
            anchor,
          );

          // do not change the original target object if pose did not change
          if (!posesAreEqual(target.calibrationPose, calibratedPose)) {
            const newJointAngles =
              await this.routineContext.getJointAnglesForCartesianSpacePose(
                calibratedPose,
                this.args.motionKind,
                false,
              );

            if (newJointAngles) {
              target.jointAngles = newJointAngles;
            } else {
              throw new Error(
                'Offset pose is unreachable: could not solve for joint angles',
              );
            }
          }
        }
      }

      if ('pose' in target) {
        poseForPreviousTarget = target.pose;
      }

      return { target, poseForPreviousTarget, poseForPreviousTargetTCPOption };
    } catch (error) {
      return {
        failure: {
          kind: FailureKind.InvalidRoutineLoadedFailure,
        },
        failureReason: error.message,
        error,
      };
    }
  }
}
